import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useForm } from '@mantine/form';
import { TextInput, PasswordInput, Button, Paper, Text, Image, Grid } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useNavigate, Link } from 'react-router-dom';
import logo from './../assets/logo.png';
import { ArrowLeft, Eye, EyeOff, Lock, Mail } from 'lucide-react';

const LoginPage: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (value.length < 5 ? 'Invalid email' : null),
      password: (value) => (value.length < 6 ? 'Password too short' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    const { error } = await signIn(values.email, values.password);

    if (error) {
      showNotification({
        title: 'Login Error',
        message: error,
        color: 'red',
      });
      return;
    }

    showNotification({
      title: 'Login Successful',
      message: 'Welcome back!',
      color: 'green',
    });

    navigate('/dashboard');
  };

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            <span className="bg-linear-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Welcome Back
            </span>
          </h2>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            Sign in to your account
          </h1>
          <p className="mt-2 text-gray-600">
            Enter your credentials to access the dashboard
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-2xl sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={form.onSubmit(handleSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  {...form.getInputProps('email')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  {...form.getInputProps('password')}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-medium"
              >
                Sign in
              </button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="ml-2 font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
            >
              Register an Account
            </button>
          </p>
        </div>
        
        {/* Back to home link */}
        <div className="mt-4 text-center">
          <a onClick={() => navigate('/')} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to home
          </a>
        </div>
      </div>
      
      {/* Background elements */}
      {/* <div className="absolute top-0 right-0 hidden lg:block w-1/3 h-full bg-gradient-to-bl from-indigo-100 to-transparent opacity-50 rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 hidden lg:block w-1/4 h-1/2 bg-gradient-to-tr from-purple-100 to-transparent opacity-50 rounded-tr-full"></div> */}
    </div>
  );
};

export default LoginPage;
